import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValid } from "date-fns";
import { useLocale } from "@/hooks/useLocale";

/**
 * Time: a relative timestamp that keeps up with the clock.
 *
 * Part of the widget primitive vocabulary — see ./index.ts. Widgets differ in
 * what they measure, not in how a number, a chart or a person-row looks, so
 * every shape lives here rather than being re-invented per widget.
 */

/**
 * A relative timestamp that keeps up with the clock.
 *
 * "2 min ago" computed during render is correct for exactly one frame and then
 * quietly lies until something unrelated re-renders the widget — which, on a
 * dashboard left open all day, may be never. This owns a minute interval so the
 * label actually advances.
 *
 * The interval lives on the leaf rather than the widget, and deliberately not in
 * a shared canvas-level clock: a shared `now` in context would re-render every
 * consumer of that context each minute, whereas this re-renders one `<time>`
 * element. A handful of independent minute timers is the cheaper trade, and the
 * component is small enough that the extra renders cost nothing.
 *
 * Minute granularity because these read "3 days ago" and "just now" far more
 * often than anything second-accurate; a faster tick would be invisible work.
 */
export const RelativeTime = ({
  date,
  prefix,
}: {
  date: string | Date;
  /** e.g. "Back", "Joined" — kept outside the label so it is not re-derived on tick. */
  prefix?: string;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const { formatRelative } = useLocale();

  const computeLabel = () => {
    if (!date) return t("company:widgets.not_available");
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (!isValid(dateObj)) return "—";
    if (Math.abs(dateObj.getTime() - Date.now()) < 60_000)
      return t("common:chrome.just_now");
    return formatRelative(dateObj);
  };

  const [label, setLabel] = useState(computeLabel);

  useEffect(() => {
    setLabel(computeLabel());
    const timer = setInterval(() => setLabel(computeLabel()), 60_000);
    return () => clearInterval(timer);
  }, [date]);

  const iso = typeof date === "string" ? date : date.toISOString();

  return (
    <time dateTime={iso}>
      {prefix
        ? t("company:widgets.prefixed_relative_time", { prefix, time: label })
        : label}
    </time>
  );
};
